/*@flow*/
import { Id, NormalizedNumber as Norm } from 'types/tcomb';
import * as t from 'lib/t';
import enumsFor from './util/enumsFor';
import * as loaders from 'queries/loaders';
import mkTagTemplate from 'lib/templates/mkTagTemplate';
import isEmbedded from 'lib/isEmbedded';

const config = {
  id: t.maybe(Id).opt({ type: 'hidden' }),
  municipalId: t.String.opt({ label: 'Municipal ID' }),
  name: t.String,
  status: t.String.options({
    template: 'PropertyStatus',
    hideWhen: isEmbedded,
    attrs: {
      placeholder: 'Choose status',
      classNamePrefix: 'Select',
    },
  }),
  address: t.maybe(t.String).options({
    template: 'LocationPickerTemplate',
  }),
  mapLabel: t.maybe(t.String),
  theGeom: t.String.options({ type: 'hidden' }),
  visibility: enumsFor('visibility'),
  category: t.maybe(t.String).opt({
    template: 'PropertyCategory',
    attrs: {
      placeholder: 'Choose a category',
    },
  }),
  tags: t.maybe(t.list(t.String)).opt({
    template: mkTagTemplate('properties'),
    attrs: {
      placeholder: 'Select...',
    },
  }),
  photoUrls: t.maybe(t.list(t.String)).options({
    label: 'Photo(s)',
    template: 'PhotoUploaderTemplate',
    help: 'Photos must be larger than 600px wide.',
  }),
  annualPropertyTax: t.maybe(t.String),
  annualDistrictAssessment: t.maybe(t.String),
  section: t.maybe(Norm),
  propertyBlockId: t.maybe(Norm).opt({ label: 'Block' }),
  propertyLotId: t.maybe(Norm).opt({ label: 'Lot' }),
  condo: t.maybe(Norm),
  owner: t.maybe(t.String),
  description: t.maybe(t.String).opt({
    type: 'textarea',
    template: 'TextEditorTemplate',
  }),
  notes: t.maybe(t.String).opt({
    type: 'textarea',
    template: 'TextEditorTemplate',
  }),
  propertyOwners: t.maybe(t.String),
  propertyClass: t.maybe(t.list(t.String)).opt({
    template: 'CreatableSelectTemplate',
    isMulti: true,
    label: 'Property Class',
    attrs: {
      placeholder: 'Select...',
      loadValues: loaders.propertyClassLoader,
      classNamePrefix: 'Select',
    },
  }),
  taxClass: t.maybe(t.list(t.String)).opt({
    template: 'CreatableSelectTemplate',
    isMulti: true,
    label: 'Tax Class',
    attrs: {
      placeholder: 'Select...',
      loadValues: loaders.taxClassLoader,
      classNamePrefix: 'Select',
    },
  }),
  districtClass: t.maybe(t.list(t.String)).opt({
    template: 'CreatableSelectTemplate',
    categories: [],
    isMulti: true,
    label: 'District Class',
    attrs: {
      placeholder: 'Select...',
      loadValues: loaders.districtClassLoader,
      classNamePrefix: 'Select',
    },
  }),
  annualDistrictAssessmentExemptions: t.maybe(Norm),
  easement: t.maybe(t.Boolean),
  totalAssessedValue: t.maybe(Norm),

  lotPerimeter: t.maybe(Norm).opt({
    label: 'Perimeter (FT)',
    before: {
      tag: 'h3',
      props: {
        children: 'Property Lot',
        className: 'mt-5',
      },
    },
  }),
  lotDepth: t.maybe(Norm).opt({ label: 'Depth (FT)' }),
  lotFront: t.maybe(Norm).opt({ label: 'Front (FT)' }),
  lotSide1: t.maybe(Norm).opt({ label: 'Side 1 (FT)' }),
  lotSide2: t.maybe(Norm).opt({ label: 'Side 2 (FT)' }),
  lotSide3: t.maybe(Norm).opt({ label: 'Side 3 (FT)' }),
  lotRear: t.maybe(Norm).opt({ label: 'Rear (FT)' }),
  lotAreaTotal: t.maybe(Norm).opt({ label: 'Lot Area Total (SQ FT)' }),

  residentialArea: t.maybe(Norm).opt({
    label: 'Residential (SQ FT)',
    before: {
      tag: 'h3',
      props: {
        children: 'Property Area',
        className: 'mt-5',
      },
    },
  }),
  commercialArea: t.maybe(Norm).opt({ label: 'Commercial (SQ FT)' }),
  retailArea: t.maybe(Norm).opt({ label: 'Retail (SQ FT)' }),
  officeArea: t.maybe(Norm).opt({ label: 'Office (SQ FT)' }),
  industrialArea: t.maybe(Norm).opt({ label: 'Industrial (SQ FT)' }),
  agriculturalArea: t.maybe(Norm).opt({ label: 'Agricultural (SQ FT)' }),
  communityArea: t.maybe(Norm).opt({ label: 'Community (SQ FT)' }),
  otherArea: t.maybe(Norm).opt({ label: 'Other (SQ FT)' }),
  totalBldgArea: t.maybe(Norm).opt({ label: 'Total (SQ FT)' }),

  billingContactName: t.maybe(t.String).opt({
    label: 'Name',
    before: {
      tag: 'h3',
      props: {
        children: 'Billing Contact',
        className: 'mt-5',
      },
    },
  }),
  billingContactEmail: t.maybe(t.String).opt({ label: 'Email' }),
  billingContactPhone: t.maybe(t.String).opt({ label: 'Phone' }),

  billingAddressStreet: t.maybe(t.String).opt({
    label: 'Street',
    before: {
      tag: 'h3',
      props: {
        children: 'Billing Address',
        className: 'mt-5',
      },
    },
  }),
  billingAddressCity: t.maybe(t.String).opt({ label: 'City' }),
  billingAddressState: t.maybe(t.String).opt({ label: 'State' }),
  billingAddressZip: t.maybe(t.String).opt({ label: 'Zip' }),
  billingAddressCountry: t.maybe(t.String).opt({ label: 'Country' }),

  contactIds: t.maybe(t.list(Id)).opt({
    before: {
      tag: 'h3',
      props: { children: 'Associated Records', className: 'mt-5' },
    },
    after: {
      tag: 'div',
      props: { className: 'mb-5 mt-5' },
    },
    template: 'Contacts',
    label: 'Contacts',
    attrs: {
      placeholder: 'Select Contact',
      classNamePrefix: 'SelectRecordContacts',
    },
    hideWhen: isEmbedded,
  }),
};
export default t.struct(config, 'Property').opt<any>({ legend: '' });
